import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  Link,
  MemoryRouter,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Textarea from "@mui/joy/Textarea";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";

// Note: This is a staging (test) URL. A separate URL will be used for production: https://super.com.
// Ensure you use the appropriate URLs for each environment.
const IFRAME_ORIGIN = "https://staging.super.com"; // http://localhost:3000

const APP_READY = "APP_READY";
const PAGE_INIT = "PAGE_INIT";
const APP_ROUTE_CHANGE = "APP_ROUTE_CHANGE";
const USER_LOGIN_REQUEST = "USER_LOGIN_REQUEST";
const USER_LOGOUT_REQUEST = "USER_LOGOUT_REQUEST";
const APP_OVERLAY_TOGGLE = "APP_OVERLAY_TOGGLE";

const Nav = ({ showLogin, onLoginClick }) => {
  return (
    <>
      {showLogin && (
        <>
          <ButtonGroup>
            <Button color="secondary" onClick={onLoginClick}>
              Login
            </Button>
          </ButtonGroup>

          <Divider sx={{ my: 2 }} />
        </>
      )}

      <ButtonGroup>
        <Button component={Link} to="/">
          Landing
        </Button>
        <Button component={Link} to="/flight">
          Search Results
        </Button>
        <Button component={Link} to="/book">
          Booking Form
        </Button>
      </ButtonGroup>
    </>
  );
};

function useGlobalDrawerObserver(onDrawerToggle) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  useEffect(() => {
    const checkDrawerState = () => {
      const presentationEl = document.querySelector('[role="presentation"]');
      const backdropEl = document.querySelector(".MuiBackdrop-root");
      const drawerEl = document.querySelector(".MuiDrawer-root");

      const hasDrawerBackdrop =
        presentationEl !== null || backdropEl !== null || drawerEl !== null;

      if (hasDrawerBackdrop !== isDrawerOpen) {
        setIsDrawerOpen(hasDrawerBackdrop);
        onDrawerToggle(hasDrawerBackdrop);
      }
    };

    const observer = new MutationObserver(() => {
      setTimeout(checkDrawerState, 10);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "class"],
    });

    return () => {
      observer.disconnect();
    };
  }, [onDrawerToggle, isDrawerOpen]);
}

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fields, setFields] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [isDrawerOpen1, setIsDrawerOpen1] = useState(false);
  const [isDrawerOpen2, setIsDrawerOpen2] = useState(false);

  const sendMessageToIframe = (kind, fields) => {
    if (window.parent) {
      console.log("IFRAME sending message", {
        kind,
        fields,
      });
      window.parent.postMessage({ kind, fields }, IFRAME_ORIGIN);
    } else {
      console.error("Unable to access window.parent");
    }
  };

  // Notify parent of route changes
  useEffect(() => {
    sendMessageToIframe(APP_ROUTE_CHANGE, { pathname: location.pathname });
  }, [location.pathname]);

  // Handle messages from parent
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== IFRAME_ORIGIN || !event.data.kind) {
        return;
      }

      console.log("IFRAME received message", {
        kind: event.data.kind,
        fields: event.data.fields,
      });

      switch (event.data.kind) {
        case PAGE_INIT:
          setFields(event.data.fields);
          if (event.data.fields?.authToken) {
            setIsAuthed(true);
          }
          break;
        case APP_ROUTE_CHANGE:
          const pathname = event.data.fields?.pathname;
          if (typeof pathname === "string") {
            if (location.pathname !== pathname) {
              navigate(pathname);
            }
          }
          break;
        case USER_LOGOUT_REQUEST:
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [navigate, location.pathname]);

  useEffect(() => {
    sendMessageToIframe(APP_READY);
  }, []);

  useGlobalDrawerObserver((isOpen) => {
    const isContentScrolled = () => {
      const scrollElement =
        document.documentElement.scrollTop > 0
          ? document.documentElement
          : document.body;
      const hasScrollableContent =
        scrollElement.scrollHeight > scrollElement.clientHeight;
      const isScrolledAtLeast1px = scrollElement.scrollTop >= 1;

      return hasScrollableContent && isScrolledAtLeast1px;
    };

    const shouldShow = isOpen && !isContentScrolled();
    const isMobile = window.innerWidth < 992;
    const headerHeight = isMobile ? "49px" : "68px";

    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.willChange = "transform";
      rootElement.style.transform = shouldShow
        ? `translateY(${headerHeight})`
        : "translateY(0)";
      rootElement.style.backgroundColor =
        isOpen && isContentScrolled() ? "white" : "transparent";
    }

    sendMessageToIframe(APP_OVERLAY_TOGGLE, { isOpen });
  });

  return (
    <>
      <Container>
        <Nav
          showLogin={!isAuthed}
          onLoginClick={() => sendMessageToIframe(USER_LOGIN_REQUEST)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography>Props:</Typography>

        <Textarea
          placeholder="Init data"
          value={JSON.stringify(fields, null, 2)}
        />

        <Divider sx={{ my: 2 }} />

        <Routes>
          <Route
            path="/"
            element={
              <>
                <Typography variant="h4">Landing</Typography>
                <Button
                  variant="contained"
                  onClick={() => setIsDrawerOpen1(true)}
                  sx={{ mr: 2 }}
                >
                  Open Drawer 1
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsDrawerOpen2(true)}
                >
                  Open Drawer 2
                </Button>

                {/* First Drawer */}
                <Drawer
                  open={isDrawerOpen1}
                  onClose={() => setIsDrawerOpen1(false)}
                  anchor="right"
                >
                  <Box sx={{ p: 2, width: 300 }}>
                    <Typography variant="h5" gutterBottom>
                      Right Drawer
                    </Typography>
                    <Typography variant="body1" paragraph>
                      This drawer appears over the parent header.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setIsDrawerOpen1(false)}
                      sx={{ mt: 2 }}
                    >
                      Close Drawer
                    </Button>
                  </Box>
                </Drawer>

                {/* Second Drawer */}
                <Drawer
                  open={isDrawerOpen2}
                  onClose={() => setIsDrawerOpen2(false)}
                  anchor="left"
                >
                  <Box sx={{ p: 2, width: 250 }}>
                    <Typography variant="h5" gutterBottom>
                      Left Drawer
                    </Typography>
                    <Typography variant="body1" paragraph>
                      This is a different drawer, but the global observer
                      catches it too!
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setIsDrawerOpen2(false)}
                      sx={{ mt: 2 }}
                    >
                      Close This One Too
                    </Button>
                  </Box>
                </Drawer>
              </>
            }
          />
          <Route
            path="/flight"
            element={
              <>
                <Typography variant="h4">Search Results</Typography>
              </>
            }
          />
          <Route
            path="/book"
            element={
              <>
                <Typography variant="h4">Booking Form</Typography>
              </>
            }
          />
        </Routes>
      </Container>
    </>
  );
};

const App = () => {
  return (
    <MemoryRouter>
      <AppContent />
    </MemoryRouter>
  );
};

export default App;
